import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import { attachIntervalToDisplayObject, CountDownFormat, formatIntoUnitCountdown } from '../../../utils/time-utils';
import type { MenuIcon } from '../../../mainMenu/types';
import { LevelLockMenuIconOverlay } from '../../../views/LevelLockMenuIconOverlay';
import { LuckyWheelMenuIconLayout } from '../../../../layouts/luckyWheel/LuckyWheelMenuIconLayout';
import type { LuckyWheelObservable } from '../types';
import { LUCKY_WHEEL_MIN_LEVEL } from '../LuckyWheelConstants';
import { getNextSpinTime, getTotalFreeSpins, isAdSpinAvailable, isFreeSpinAvailable, isLuckyWheelAvailable } from '../LuckyWheelUtils';
import { applyLayoutState, resetLayout,  } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import { createLayoutAnimationPlayer } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import type { LayoutAnimationPlayer } from '../../../libs/layouts/layoutAnimations/LayoutAnimationPlayer';

export class LuckyWheelMenuIconView extends Container implements MenuIcon {
  public readonly onVisibilityChanged = new Signal<(visible: boolean) => void>();

  public readonly onClicked = new Signal();

  private readonly layout = new LuckyWheelMenuIconLayout();

  private idleAnimator: LayoutAnimationPlayer | null = null;

  private cancelTimeUpdate: (() => void) | null = null;

  constructor(private readonly observable: LuckyWheelObservable) {
    super();

    if (isLuckyWheelAvailable(observable)) {
      this.init();
    } else {
      this.initLocked();
    }
  }

  private init(): void {
    const { root } = this.layout;

    root.props({
      parent: this,
    });

    root.on('pointertap', () => {
      this.onClicked.emit();
    });

    this.observable.addChangeHandler(this, () => {
      this.refreshIconState();
    });
  }

  private initLocked(): void {    
    const { root } = this.layout;

    root.props({
      parent: this,
    });

    new LevelLockMenuIconOverlay(LUCKY_WHEEL_MIN_LEVEL).props({
      parent: this,
    });

    this.setIconLabelVisibility(false);
    this.updateNotificationAmount(0);
  }

  private refreshIconState(): void {
    this?.cancelTimeUpdate?.();

    this.setIconLabelVisibility(true);
    this.updateNotificationAmount(getTotalFreeSpins(this.observable));

    resetLayout(this.layout);
    this.idleAnimator?.setPause(true);
    this.idleAnimator?.destroy();

    if (isFreeSpinAvailable(this.observable)) {
      this.showFreeSpin();
    } else if (isAdSpinAvailable(this.observable)) {
      this.showAdSpins();
    } else {
      this.showTimer();
    }
  }

  private showFreeSpin(): void {
    applyLayoutState(this.layout, 'FreeSpin');
    this.idleAnimator = createLayoutAnimationPlayer(this.layout, 'Free_Spin_Idle');
    this.idleAnimator.play(true);
  }

  private showAdSpins(): void {
    const { luckyWheel } = this.observable.getValue();
    const { adCountText } = this.layout;

    applyLayoutState(this.layout, 'AdSpins');
    this.idleAnimator = createLayoutAnimationPlayer(this.layout, 'Ad_Spin_Idle');
    this.idleAnimator.play(true);

    adCountText.text = getLocalizedText('<amount> left').replace(
      '<amount>',
      `${luckyWheel.adSpinCount}`,
    );
  }

  private showTimer(): void {
    const { luckyWheel } = this.observable.getValue();
    const { timerText } = this.layout;

    applyLayoutState(this.layout, 'NoSpin');

    this.cancelTimeUpdate = attachIntervalToDisplayObject(
      timerText,
      () => {
        const nextSpinTime = getNextSpinTime(luckyWheel.lastFreeSpinTime);
        const timeLeft = nextSpinTime - Date.now();

        timerText.text = formatIntoUnitCountdown(timeLeft, 2, CountDownFormat.DHM);
        if (timeLeft <= 0) this.refreshIconState();
      },
      1000,
    );
  }

  private setIconLabelVisibility(value: boolean): void {
    const { timerContainer } = this.layout;
    timerContainer.visible = value;
  }

  private updateNotificationAmount(amount: number): void {
    const { notificationDot, notificationAmount } = this.layout;

    notificationDot.visible = amount > 1;
    notificationAmount.text = amount;
  }

  public isVisible(): boolean {
    return this.visible;
  }
}
